import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useClientAccess } from '../hooks/useClientAccess';
import { statusService, SystemStatus, SystemStatusSummary } from '../services/statusService';
import { safeStorage } from '../utils/safeStorage';
import { isAbortError } from '../utils/abortError';
import { DeviceTypePie } from '../components/common/DeviceTypePie';
import '../styles/dashboard.css';

export const DashboardPage: React.FC = () => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { shouldShowClientUI } = useClientAccess();
  const { formatDateTime, formatTime, t } = useI18n();
  // `status` holds whichever payload is currently driving the UI. On
  // first load we populate it with the lightweight summary (cards +
  // device-type chart) so the dashboard renders in a few hundred ms
  // even at production scale. If the viewer has multi-client access
  // we kick off a second background fetch for the full payload (with
  // the per-client stats table) and swap it in when it arrives — this
  // is what used to block the entire page for 30+ seconds.
  const [status, setStatus] = useState<SystemStatus | SystemStatusSummary | null>(null);
  const [clientsStatsLoading, setClientsStatsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const hasFetched = useRef(false);
  const fetchAttempts = useRef(0);

  const fetchStatus = useCallback(async (signal?: AbortSignal) => {
    // Check if we have a valid token before fetching
    const token = safeStorage.getItem('access_token');
    if (!token) {
      return false;
    }

    try {
      setLoading(true);
      setError(null);
      // Fast path: always fetch the summary first so the cards render
      // immediately. ~200 B response, one aggregation query, cached
      // for 120 s.
      const summary = await statusService.getSystemStatusSummary({ signal });
      if (signal?.aborted) return false;
      setStatus(summary);
      setLastUpdated(new Date());
      hasFetched.current = true;
      fetchAttempts.current = 0;
      setLoading(false);

      // Slow path (only for multi-client viewers): fetch the full
      // payload with per-client stats in the background and swap it
      // in when it arrives. This decouples the client-stats table
      // from the page's initial paint.
      if (shouldShowClientUI) {
        setClientsStatsLoading(true);
        statusService
          .getSystemStatus({ signal })
          .then((full) => {
            if (!signal?.aborted) setStatus(full);
          })
          .catch((err) => {
            if (isAbortError(err)) return;
            console.error('Failed to fetch full dashboard stats:', err);
          })
          .finally(() => {
            if (!signal?.aborted) setClientsStatsLoading(false);
          });
      }
      return true;
    } catch (err: any) {
      if (isAbortError(err)) return false;
      console.error('Failed to fetch system status:', err);
      // If we get 401 and haven't retried too many times, the token might not be ready yet
      if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        fetchAttempts.current++;
        if (fetchAttempts.current < 3) {
          setLoading(false);
          return false;
        }
      }
      setError('Failed to load dashboard data');
      setLoading(false);
      return false;
    }
  }, [shouldShowClientUI]);

  useEffect(() => {
    // Reset fetch flag when auth state changes
    if (!isAuthenticated) {
      hasFetched.current = false;
      fetchAttempts.current = 0;
      return;
    }

    // Only fetch when authenticated, auth is not loading, AND user is set
    if (isAuthenticated && !authLoading && user) {
      const ac = new AbortController();
      // Fetch immediately if we haven't fetched successfully yet
      if (!hasFetched.current) {
        // Small delay to ensure token is properly stored in localStorage
        const timeoutId = setTimeout(async () => {
          const success = await fetchStatus(ac.signal);
          // If fetch failed with 401, retry once more after a delay
          if (!success && fetchAttempts.current > 0 && fetchAttempts.current < 3) {
            setTimeout(() => {
              if (!ac.signal.aborted) fetchStatus(ac.signal);
            }, 500);
          }
        }, 100);

        return () => {
          clearTimeout(timeoutId);
          ac.abort();
        };
      }

      // Auto-refresh every 2 minutes. /v2/status is the most expensive
      // read in the system (aggregates 250k+ devices) — the previous 30 s
      // cadence multiplied backend load by 4x for no real user benefit
      // since the dashboard is directional, not real-time.
      const interval = setInterval(() => fetchStatus(ac.signal), 120000);
      return () => {
        clearInterval(interval);
        ac.abort();
      };
    }
  }, [isAuthenticated, authLoading, user, fetchStatus]);

  const formatTimestamp = (timestamp: string) => formatDateTime(timestamp);

  const getDeviceTypeLabel = (type: string): string => {
    const labels: { [key: string]: string } = {
      'PGA': 'PGA',
      'P4GU': 'P4GU',
      'IOTL': 'IoT Gateway',
      'TMS2': 'TMS2',
      'UNKN': 'Unknown'
    };
    return labels[type] || type;
  };

  const getDeviceTypeColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      'PGA': '#3b82f6',
      'P4GU': '#8b5cf6',
      'IOTL': '#10b981',
      'TMS2': '#f59e0b',
      'UNKN': '#6b7280'
    };
    if (colors[type]) return colors[type];
    // Deterministic HSL fallback so a catalog with dozens of hardware
    // types doesn't collapse onto the same gray. Hue spread across the
    // wheel, saturation + lightness tuned for the donut chart.
    let h = 0;
    for (let i = 0; i < type.length; i++) {
      h = (h * 31 + type.charCodeAt(i)) | 0;
    }
    return `hsl(${Math.abs(h) % 360} 65% 55%)`;
  };

  if (authLoading || (loading && !status)) {
    return (
      <div className="page dashboard-page">
        <div className="page-header">
          <div className="page-title">
            <h1>Dashboard</h1>
            <p>System Overview & Statistics</p>
          </div>
        </div>
        <div className="page-content">
          <div className="dashboard-loading">
            <div className="loading-spinner"></div>
            <p>{authLoading ? 'Authenticating...' : 'Loading dashboard data...'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page dashboard-page">
        <div className="page-header">
          <div className="page-title">
            <h1>Dashboard</h1>
            <p>System Overview & Statistics</p>
          </div>
        </div>
        <div className="page-content">
          <div className="dashboard-error">
            <span className="error-icon">!</span>
            <h3>{error}</h3>
            <button onClick={() => fetchStatus()} className="retry-button">Retry</button>
          </div>
        </div>
      </div>
    );
  }

  if (!status) return null;

  return (
    <div className="page dashboard-page">
      <div className="page-header">
        <div className="page-title">
          <h1>Dashboard</h1>
          <p>System Overview & Statistics</p>
        </div>
        <div className="page-actions">
          <div className="last-updated">
            Last updated: {formatTime(lastUpdated)}
          </div>
          <button onClick={() => fetchStatus()} className="refresh-button" disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="page-content dashboard-content">
        {/* Key Metrics Cards */}
        <div className="stats-grid">
          <div className="stat-card primary">
            <div className="stat-icon">📱</div>
            <div className="stat-content">
              <div className="stat-value">{status.total_devices}</div>
              <div className="stat-label">{t('devices.totalDevices')}</div>
            </div>
            {status.recent_devices_count > 0 && (
              <div className="stat-badge positive">+{status.recent_devices_count} this week</div>
            )}
          </div>

          {shouldShowClientUI && (
            <div className="stat-card secondary">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <div className="stat-value">{status.total_clients}</div>
                <div className="stat-label">{t('clients.totalClients')}</div>
              </div>
              <div className="stat-meta">{status.active_clients} active</div>
            </div>
          )}

          <div className="stat-card success">
            <div className="stat-icon">✓</div>
            <div className="stat-content">
              <div className="stat-value">
                {status.total_devices - status.devices_needing_refresh}
              </div>
              <div className="stat-label">{t('devices.activeDevices')}</div>
            </div>
            <div className="stat-meta">
              {status.total_devices > 0
                ? Math.round(((status.total_devices - status.devices_needing_refresh) / status.total_devices) * 100)
                : 0}% uptime
            </div>
          </div>

          <div className="stat-card warning">
            <div className="stat-icon">⚠️</div>
            <div className="stat-content">
              <div className="stat-value">{status.devices_needing_refresh}</div>
              <div className="stat-label">{t('devices.needsRefresh')}</div>
            </div>
            {status.recent_updates_count > 0 && (
              <div className="stat-badge">{status.recent_updates_count} updated today</div>
            )}
          </div>
        </div>

        {/* Device Type Distribution */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>{t('dashboard.deviceTypeDistribution')}</h2>
            <p>{t('dashboard.breakdownByType')}</p>
          </div>
          <DeviceTypePie
            total={status.total_devices}
            slices={status.devices_by_type.map(t => ({
              type: t.device_type,
              label: getDeviceTypeLabel(t.device_type),
              count: t.count,
              color: getDeviceTypeColor(t.device_type),
            }))}
          />
        </div>

        {/* Client Statistics - Only shown for users with multi-client access.
            Loaded asynchronously after the cards so it doesn't block
            the dashboard's first paint. `clients_stats` only exists
            on the full SystemStatus response (not SystemStatusSummary). */}
        {shouldShowClientUI && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>{t('dashboard.clientStatistics')}</h2>
              <p>{t('dashboard.deviceBreakdownByClient')}</p>
            </div>
            <div className="clients-stats-table">
              {clientsStatsLoading && !(status as SystemStatus).clients_stats ? (
                <div className="empty-state">Loading client statistics…</div>
              ) : (status as SystemStatus).clients_stats && (status as SystemStatus).clients_stats.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th>{t('clients.clientName')}</th>
                      <th>{t('devices.totalDevices')}</th>
                      <th>{t('common.active')}</th>
                      <th>{t('devices.needsRefresh')}</th>
                      <th>{t('dashboard.deviceTypesColumn')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(status as SystemStatus).clients_stats
                      .slice()
                      .sort((a, b) => b.total_devices - a.total_devices)
                      .map((client) => (
                        <tr key={client.client_id}>
                          <td className="client-name-cell">
                            <strong>{client.client_name}</strong>
                          </td>
                          <td className="number-cell">{client.total_devices}</td>
                          <td className="number-cell success-text">{client.active_devices}</td>
                          <td className="number-cell warning-text">{client.devices_needing_refresh}</td>
                          <td className="device-types-cell">
                            {client.devices_by_type.length > 0 ? (
                              <div className="device-types-chips">
                                {client.devices_by_type.map((type) => (
                                  <span
                                    key={type.device_type}
                                    className="device-type-chip"
                                    style={{
                                      backgroundColor: getDeviceTypeColor(type.device_type) + '20',
                                      color: getDeviceTypeColor(type.device_type)
                                    }}
                                  >
                                    {getDeviceTypeLabel(type.device_type)}: {type.count}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="empty-value">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state">{t('dashboard.noClientsToDisplay')}</div>
              )}
            </div>
          </div>
        )}

        {/* Recent Activity Summary */}
        <div className="dashboard-footer">
          <div className="activity-summary">
            <div className="activity-item">
              <span className="activity-icon">📅</span>
              <span className="activity-text">
                <strong>{status.recent_devices_count}</strong> devices added in the last 7 days
              </span>
            </div>
            <div className="activity-item">
              <span className="activity-icon">🔄</span>
              <span className="activity-text">
                <strong>{status.recent_updates_count}</strong> devices updated in the last 24 hours
              </span>
            </div>
            <div className="activity-item">
              <span className="activity-icon">⏰</span>
              <span className="activity-text">
                Data timestamp: <strong>{formatTimestamp(status.timestamp)}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
